<script>
    import {onMount} from 'svelte';

    import {mapStore} from '@js/stores/mapStore';
    import {userStore} from '@js/stores/userStore';

    import I18n from '@js/modules/translations';
    import StorageService from '@js/modules/storage';
    import AnalyticsService from '@js/modules/analyticsService';

    import {
        HomeOnboardingSteps,
        HomeOnboardingEnabled,
        HomeOnboardingStorageKey
    } from '@js/components/modules/constants';

    // Sections to spotlight per step (only present once a route is started).
    const HIGHLIGHT_TARGET = {
        data:     '#home-route-details',
        features: '#home-route-info',
        save:     '#home-ride-creation'
    };

    const stored = StorageService.getItem(HomeOnboardingStorageKey) || {};

    let step = $state(stored.step || 'draw');
    let dismissed = $state(!!stored.dismissed);
    let completed = $state(!!stored.completed);
    // Persisted so a page reload does not re-fire a step's "shown" event.
    let shownSteps = $state(Array.isArray(stored.shown) ? stored.shown : []);

    // Connected users who already saved a route don't need the onboarding.
    const savedRide = !!window.userHasSavedRide;
    let highlightTimer;

    const _persist = () => StorageService.setItem(HomeOnboardingStorageKey, {step, dismissed, completed, shown: shownSteps});

    const eligible = $derived(
        HomeOnboardingEnabled && !dismissed && !completed && (!$userStore.isConnected || !savedRide)
    );

    const routeStarted = $derived(!!$mapStore.currentDistance);

    // No route yet -> "draw"; once a route exists -> at least "data".
    const visibleStep = $derived(!routeStarted ? 'draw' : (step === 'draw' ? 'data' : step));

    // Suppress the standalone activation nudge while the onboarding is guiding the user.
    $effect(() => {
        userStore.onboardingActive = eligible;
    });

    // Auto-advance from "draw" to "data" the moment a route is started.
    $effect(() => {
        if (routeStarted && step === 'draw') {
            step = 'data';
            _persist();
        }
    });

    // Track each step once (persisted) and ring its target — WITHOUT scrolling, so the
    // step change (esp. the auto draw->data while the user is drawing) never yanks the
    // viewport away from the map. Scrolling only happens on an explicit user action.
    $effect(() => {
        if (!eligible) {
            return;
        }

        const current = visibleStep;
        if (!shownSteps.includes(current)) {
            shownSteps = [...shownSteps, current];
            _persist();
            AnalyticsService.trackOnboardingStep(current, 'shown');
        }

        _ring(current);
    });

    const _target = (current) => {
        const selector = HIGHLIGHT_TARGET[current];
        return selector ? document.querySelector(selector) : null;
    };

    const _ring = (current) => {
        const wrapper = _target(current);
        const element = wrapper?.firstElementChild || wrapper;
        if (!element) {
            return;
        }

        element.classList.add('onboarding-highlight');
        clearTimeout(highlightTimer);
        highlightTimer = setTimeout(() => element.classList.remove('onboarding-highlight'), 2600);
    };

    // Explicit user action: bring the section to the centre (above the bottom card) + ring.
    const _scrollTo = (current) => {
        const element = _target(current);
        if (!element) {
            return;
        }

        element.scrollIntoView({behavior: 'smooth', block: 'center'});
        _ring(current);
    };

    const _next = () => {
        AnalyticsService.trackOnboardingStep(visibleStep, 'next');

        const index = HomeOnboardingSteps.indexOf(visibleStep);
        step = HomeOnboardingSteps[Math.min(index + 1, HomeOnboardingSteps.length - 1)];
        _persist();
        _scrollTo(step);
    };

    // Let the user jump to any step by clicking the progress dots. Before, the dots were
    // decorative and the only way forward was the "Next" button, which was easy to miss.
    const _goTo = (target) => {
        if (target === visibleStep) {
            return;
        }

        AnalyticsService.trackOnboardingStep(visibleStep, 'next');
        step = target;
        _persist();
        _scrollTo(target);
    };

    const _dismiss = () => {
        AnalyticsService.trackOnboardingStep(visibleStep, 'dismissed');
        dismissed = true;
        _persist();
    };

    const _complete = () => {
        if (completed) {
            return;
        }

        AnalyticsService.trackOnboardingStep('save', 'completed');
        completed = true;
        _persist();
    };

    onMount(() => {
        const onRideSaved = () => _complete();
        window.addEventListener('mapplanner:ride-saved', onRideSaved);

        return () => {
            window.removeEventListener('mapplanner:ride-saved', onRideSaved);
            clearTimeout(highlightTimer);
            userStore.onboardingActive = false;
        };
    });
</script>

{#if eligible}
    <div class="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 sm:max-w-sm z-[1050] rounded-xl border border-primary-200 bg-white shadow-lg p-4 dark:bg-gray-800 dark:border-gray-700">
        <div class="flex items-start justify-between gap-3">
            <div>
                <p class="text-sm font-semibold text-primary-800 dark:text-primary-300">
                    {I18n.t(`js.onboarding.home.${visibleStep}.title`)}
                </p>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {I18n.t(`js.onboarding.home.${visibleStep}.text`)}
                </p>
            </div>
            <button type="button"
                    aria-label="dismiss"
                    class="shrink-0 text-lg leading-none text-gray-400 hover:text-gray-600"
                    onclick={_dismiss}>
                &times;
            </button>
        </div>

        <div class="mt-3 flex items-center justify-between">
            <div class="flex gap-1">
                {#each HomeOnboardingSteps as onboardingStep (onboardingStep)}
                    <button type="button"
                            aria-label={onboardingStep}
                            class="h-1.5 w-4 rounded-full cursor-pointer transition-colors"
                            class:bg-primary-500={HomeOnboardingSteps.indexOf(visibleStep) >= HomeOnboardingSteps.indexOf(onboardingStep)}
                            class:bg-gray-200={HomeOnboardingSteps.indexOf(visibleStep) < HomeOnboardingSteps.indexOf(onboardingStep)}
                            onclick={() => _goTo(onboardingStep)}></button>
                {/each}
            </div>

            {#if visibleStep === 'save'}
                <button type="button"
                        class="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
                        onclick={() => _scrollTo('save')}>
                    {I18n.t('js.onboarding.home.save.cta')}
                </button>
            {:else if visibleStep !== 'draw'}
                <button type="button"
                        class="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
                        onclick={_next}>
                    {I18n.t('js.onboarding.home.next')}
                </button>
            {/if}
        </div>
    </div>
{/if}

<style>
    :global(.onboarding-highlight) {
        outline: 2px solid #f59e0b;
        outline-offset: 2px;
        border-radius: 10px;
        box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.12);
        transition: outline-color 0.2s ease, box-shadow 0.2s ease;
    }
</style>
